/**
 * OverviewTab — Super Admin redesign v2
 * 5 stat tiles · Org health table · Audit log panel
 */
import React, { useEffect, useState } from 'react';
import { getOverview } from '../../API/adminAPI';

const AUDIT_COLORS = {
  edit:   'var(--accent)',
  create: 'var(--online)',
  delete: 'var(--offline)',
  export: 'var(--ink-300)',
};

function StatTile({ label, value, sub, color = 'var(--ink-900)', alert }) {
  return (
    <div style={{ background: alert ? 'var(--warn-bg)' : 'var(--bg-surface)', border: `1px solid ${alert ? 'var(--warn-border)' : 'var(--border-hair)'}`, borderRadius: 'var(--r-lg)', padding: '18px 20px', boxShadow: 'var(--sh-sm)' }}>
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--ink-500)', marginBottom: 8 }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 32, fontWeight: 500, color, letterSpacing: '-1px' }}>{value ?? '—'}</div>
      {sub && <div style={{ fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--ink-500)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function HealthBar({ pct = 0 }) {
  const color = pct >= 95 ? 'var(--online)' : pct >= 70 ? 'var(--warn)' : 'var(--offline)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: 'var(--border-subtle)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4, transition: 'width 0.6s ease' }} />
      </div>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color, minWidth: 32, textAlign: 'right' }}>{pct}%</span>
    </div>
  );
}

const OverviewTab = () => {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    getOverview()
      .then(setData)
      .catch(e => setError(e?.response?.data?.error || 'Failed to load overview'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--ink-300)', fontFamily: 'var(--font-sans)', fontSize: 13 }}>Loading…</div>;
  if (error)   return <div style={{ background: 'var(--offline-bg)', color: 'var(--offline)', borderRadius: 'var(--r-md)', padding: '10px 14px', fontSize: 13 }}>{error}</div>;

  const orgs  = data.org_health  || [];
  const audit = data.audit_log   || [];

  return (
    <div>
      {/* 5 stat tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 14, marginBottom: 28 }}>
        <StatTile label="Organisations" value={data.total_orgs}      />
        <StatTile label="Machines"      value={data.total_machines}  />
        <StatTile label="Online"        value={data.active_machines} color="var(--online)"  sub={`of ${data.total_machines}`} />
        <StatTile label="Users"         value={data.total_users}     />
        <StatTile label="Open Alerts"   value={data.open_alerts}     color="var(--warn)" alert={data.open_alerts > 0} />
      </div>

      {/* Body — two columns */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 20 }}>

        {/* Org health table */}
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-hair)', borderRadius: 'var(--r-lg)', overflow: 'hidden', boxShadow: 'var(--sh-sm)' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-hair)' }}>
            <div style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 14, color: 'var(--ink-900)' }}>Organisations</div>
            <div style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--ink-500)', marginTop: 2 }}>Health = machines online in last 24h</div>
          </div>
          {/* Table header */}
          <div style={{ display: 'grid', gridTemplateColumns: '24px 1fr 80px 60px 60px 120px', gap: 8, padding: '10px 20px', borderBottom: '1px solid var(--border-hair)' }}>
            {['', 'Organisation', 'Type', 'Mach.', 'Users', 'Health'].map((h, i) => (
              <div key={i} style={{ fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--ink-500)' }}>{h}</div>
            ))}
          </div>
          {orgs.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--ink-300)', fontFamily: 'var(--font-sans)', fontSize: 13 }}>No organisations yet</div>
          ) : orgs.map((o, i) => (
            <div key={o.id} style={{ display: 'grid', gridTemplateColumns: '24px 1fr 80px 60px 60px 120px', gap: 8, padding: '12px 20px', borderBottom: i < orgs.length - 1 ? '1px solid var(--border-hair)' : 'none', alignItems: 'center', opacity: o.is_active ? 1 : 0.5 }}>
              <div style={{ width: 12, height: 12, borderRadius: 3, background: o.color, flexShrink: 0 }} />
              <div>
                <div style={{ fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500, color: 'var(--ink-900)' }}>{o.name}</div>
                {!o.is_active && <div style={{ fontFamily: 'var(--font-sans)', fontSize: 10, color: 'var(--offline)' }}>Suspended</div>}
              </div>
              <div style={{ fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--ink-500)', background: 'var(--bg-raised)', borderRadius: 4, padding: '2px 7px', display: 'inline-block', textTransform: 'capitalize' }}>{o.org_type}</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--ink-700)' }}>{o.machines}</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--ink-700)' }}>{o.users}</div>
              <HealthBar pct={o.health_pct} />
            </div>
          ))}
        </div>

        {/* Audit log */}
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-hair)', borderRadius: 'var(--r-lg)', overflow: 'hidden', boxShadow: 'var(--sh-sm)' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-hair)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 14, color: 'var(--ink-900)' }}>Audit log</div>
              <div style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--ink-500)', marginTop: 2 }}>today</div>
            </div>
          </div>
          <div style={{ overflowY: 'auto', maxHeight: 420 }}>
            {audit.length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--ink-300)', fontFamily: 'var(--font-sans)', fontSize: 13 }}>No recent activity</div>
            ) : audit.map((a, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '11px 20px', borderBottom: i < audit.length - 1 ? '1px solid var(--border-hair)' : 'none' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: AUDIT_COLORS[a.kind] || 'var(--ink-300)', flexShrink: 0, marginTop: 5 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--ink-700)', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.action}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-500)', marginTop: 2 }}>
                    {a.who} · {a.when}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
